import React from 'react';
import * as R from 'ramda';
import {connect} from 'react-redux';
import Bluebird from 'bluebird';
import moment from 'moment';
import {FeatureFlag} from 'react-launch-darkly';

import ApprovalAndListViewToggle from 'js/components/approval_and_list_view_toggle';
import CampaignContentProgress from 'js/components/campaign_content_progress';
import SubmittedContentLabels from 'js/components/submitted_content_labels';
import ContentReviewWorkflow from 'js/components/content_review_workflow';
import LoadingSpinner from 'js/components/loading_spinner';
import NoPendingContent from 'js/components/no_pending_content';
import NoSubmittedContent from 'js/components/no_submitted_content';
import {getAuth0Storage} from '../../auth0_storage';
import {queryEditRequest} from '../../graphql/queries/queryEditRequest';
import {
    mutationEditRequestCreate,
    mutationInternalMessageCreate
} from '../../graphql/mutations/mutationEditRequestCreate';
import {sendBrandContentFeedbackEmail} from '../brand_api_client';
import ExtendedApprovalFlow from '../../approval_queues/components/ExtendedApprovalFlow';

import * as CampaignDucks from './ducks/campaigns';

import 'scss/layout.scss';
import 'scss/review-content.scss';
import classNames from 'classnames';

class ReviewContent extends ExtendedApprovalFlow {
    static defaultProps = {
        campaign_content_progress: {},
        campaign_content: null,
        campaign_guidelines: null,
        campaign: {}
    };

    constructor(props) {
        super(props);
        this.state = {
            isLoading: R.isNil(props.campaign_content),
            skipped: [],
            editRequest: null,
            internalMessages: null
        };
    }

    componentDidMount() {
        this.fetchCampaign();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const {
            fetchCampaignContentProgress,
            campaign_content,
            params: {campaignSlug}
        } = this.props;

        if (R.isNil(campaign_content) && R.isEmpty(nextProps.campaign_content)) {
            // The content loaded, but there's nothing to show. Update the progress bar
            // in case its pending count is outdated.
            fetchCampaignContentProgress(campaignSlug);
        }
    }

    fetchCampaign = async () => {
        const {
            fetchCampaign,
            fetchCampaignContentProgress,
            fetchCampaignContent,
            fetchCampaignGuidelines,
            fetchOldestCampaignContent,
            params: {campaignSlug, contentMetadataId},
            routeParams: {contentMetadataId: routeContentMetadataId},
        } = this.props;

        await this.setStateAsync({isLoading: true});
        await Bluebird.all([
            fetchCampaign(campaignSlug),
            fetchCampaignContentProgress(campaignSlug),
            contentMetadataId || routeContentMetadataId
                ? fetchCampaignContent(campaignSlug, contentMetadataId || routeContentMetadataId)
                : fetchOldestCampaignContent(campaignSlug),
            fetchCampaignGuidelines(campaignSlug)
        ]);
        const requests = await this.getEditRequest(this.props.campaign_content.id);
        if (requests?.editRequest?.length)
            await this.setStateAsync({editRequest: requests.editRequest[0]});
        if (requests?.internalMessages?.length)
            await this.setStateAsync({internalMessages: requests.internalMessages});
        await this.setStateAsync({isLoading: false});
    };

    /**
     * Use me if you have an api call to make that you want to disable
     * other components that spawn api calls
     * */
    performWithTransmissionState = async fn => {
        this.setState({isLoading: true});
        await fn();
        this.setState({isLoading: false});
    };

    setStateAsync(state) {
        return new Promise(resolve => {
            this.setState(state, resolve);
        });
    }

    nextContent = async () => {
        await this.setStateAsync(prevState => ({
        skipped: [
                ...prevState.skipped, 
                this.props.campaign_content.id], 
                editRequest: null, 
                internalMessages: null
        }));
        await this.gotoNextContent();
    };

    prevContent = async () => {
        await this.setStateAsync(prevState => {

            const skippedContent = 
                prevState.skipped.length ? [...prevState.skipped.slice(0,-1)] : []
            return {skipped: [...skippedContent], editRequest: null, internalMessages: null}
        });
        await this.gotoNextContent();
    };

    gotoNextContent = async () => {
        const {
            fetchOldestCampaignContent,
            params: {campaignSlug}
        } = this.props;

        await this.setStateAsync({isLoading: true});
        await fetchOldestCampaignContent(campaignSlug, this.state.skipped);
        const requests = await this.getEditRequest(this.props.campaign_content.id);
        if (requests?.editRequest?.length)
            await this.setStateAsync({editRequest: requests.editRequest[0]});
        if (requests?.internalMessages?.length)
            await this.setStateAsync({internalMessages: requests.internalMessages});
        await this.setStateAsync({isLoading: false});
    };

    gotoListView = async () => {
        const {
            gotoListView,
            params: {campaignSlug}
        } = this.props;

        await this.setStateAsync({isLoading: true});
        gotoListView(campaignSlug);
        await this.setStateAsync({isLoading: false});
    };

    onApprove = async (data, isTiktokCSFEnabled) => {
        const {
            approveContent,
            params: {contentMetadataId},
            routeParams: {contentMetadataId: routeContentMetadataId},
            campaign_content
        } = this.props;

        const content = {...campaign_content};

        await this.performWithTransmissionState(async () => {
            await approveContent(contentMetadataId || routeContentMetadataId, data);

            if (isTiktokCSFEnabled && this.isContentVideo(content.content_type)) {
                await this.handleUploadToTiktokCSF(content);
            } else {
                await this.handleLinkContentToCampaign(content);
            }

            await this.gotoNextContent();
        });
    };

    onReject = async missedGuidelines => {
        const {
            rejectContent,
            campaign_content: {id}
        } = this.props;

        await this.performWithTransmissionState(async () => {
            await rejectContent(id, {
                missedGuidelines: R.map(R.prop('guideline'), missedGuidelines)
            });
            await this.gotoNextContent();
        });
    };

    onDoNotDistribute = async ({selectedOptions, feedbackText}, isTiktokCSFEnabled) => {
        const {
            doNotDistributeContent,
            campaign_content
        } = this.props;

        const content = {...campaign_content};

        await this.performWithTransmissionState(async () => {
            // In case with the doNotDistributeContent call,
            // we need to handle the link content to campaign or upload to TikTok CSF first,
            // because after the call, the content will not be accessible anymore
            if (isTiktokCSFEnabled && this.isContentVideo(content.content_type)) {
                await this.handleUploadToTiktokCSF(content);
            } else {
                await this.handleLinkContentToCampaign(content);
            }
            await doNotDistributeContent(content.id, {
                reasons: selectedOptions,
                feedback: feedbackText
            });
            await this.gotoNextContent();
        });
    };

    handleLinkContentToCampaign = async (content) => {
        try {
            const {campaign} = this.props;

            const linkContentToCampaignPayload = {
                sn_media_id: content.media_id,
                sn_brand_id: campaign.brandId,
                sn_campaign_id: campaign.campaignId,
                sn_content_type: content.content_type.toUpperCase()
            };

            const linkContentResult = await this.linkContentToCampaign(
                linkContentToCampaignPayload,
                campaign.campaignContentWorkflow
            );

            if (
                linkContentResult
                && !linkContentResult.wasSuccessful
                && linkContentResult.error
            ) {
                console.error(linkContentResult.error);
            }

            return linkContentResult;
        } catch (error) {
            console.error('Error linking content to campaign', error);
        }

        return Promise.resolve();
    };

    handleUploadToTiktokCSF = async (content) => {
        try {
            const {campaign} = this.props;

            const tiktokCSFPayload = {
                sn_media_id: content.media_id,
                sn_campaign_name: campaign.campaignName,
                sn_brand_id: campaign.brandId,
                sn_campaign_id: campaign.campaignId
            };

            const uploadToTikTokResult = await this.uploadContentToTikTokCSF(
                tiktokCSFPayload,
                content.content_type,
                campaign.campaignContentWorkflow
            );

            if (
                uploadToTikTokResult
                && !uploadToTikTokResult.wasSuccessful
                && uploadToTikTokResult.error
            ) {
                console.error(uploadToTikTokResult.error);
            }

            return uploadToTikTokResult;
        }
        catch (error) {
            console.error('Error uploading content to TikTok CSF', error);
        }

        return Promise.resolve();
    }

    onRequestSubmit = async (message, emails) => {
        const {
            params: {campaignSlug}
        } = this.props;
        const result = await mutationEditRequestCreate(
            {
                contentId: this.props.campaign_content.id,
                message: message
            });
        if(result?.data?.editRequestCreate?.wasSuccessful === true) {
            const {auth0Profile} = getAuth0Storage();
            const newEditRequest = {
                message: message,
                createdAt: moment().format(),
                emailAddress: auth0Profile.email
            }
            this.setState({editRequest: newEditRequest})
            await sendBrandContentFeedbackEmail({
                contentId: this.props.campaign_content.id,
                campaignSlug: campaignSlug,
                emails: emails,
                editRequest: message
            });
        }
    }

    onInternalMessageSubmit = async (message) => {
        const {
            params: {campaignSlug}
        } = this.props;
        const result = await mutationInternalMessageCreate(
            {
                contentId: this.props.campaign_content.id,
                message: message
            });
        if(result?.data?.internalMessageCreate?.wasSuccessful === true) {
            const requests = await this.getEditRequest(this.props.campaign_content.id);
            if (requests?.internalMessages?.length)
                this.setState({internalMessages: requests?.internalMessages})
        }
    }

    getEditRequest = (contentId) => {
        return queryEditRequest(contentId).then((res) => {
            return {
                editRequest: res?.editRequests,
                internalMessages: res?.internalMessages
            };
        }).catch(error => console.error('Request edit get error: ', error));
    }

    render() {
        const {
            campaign_content_progress: {approved, pending, goal},
            campaign_content,
            campaign_guidelines,
            campaign: { brandEmails, isEditsAllowed},
            is_content_approval_transmitting
        } = this.props;
        const {isLoading, editRequest, internalMessages} = this.state;

        const {
            id,
            platform,
            username,
            profile_image_url,
            content_url,
            caption,
            brand_approval_due_date,
            isLast,
            isFirst
        } = campaign_content || {};

        const header = (
            <div className={classNames(
                'layout-spread review-content__header',
                {'review-content__header--no-border': !id}
            )}>
                <SubmittedContentLabels submittedContent={approved + pending} />
                <ApprovalAndListViewToggle approval onListView={this.gotoListView} />
            </div>
        );
        const progress = this.props.campaign_content_progress ? (
            <div className='content-progress'>
                <CampaignContentProgress
                    approved={approved || 0}
                    pending={pending || 0}
                    required={goal || 0}
                />
            </div>
        ) : null;

        if (isLoading) {
            return (
                <div className="review-content">
                    <div className="review-content__panel">
                        <div className='review-content__header_wrapper'>
                            <div className="review-content__header">
                                <p className="review-content__header__text">Content Approval</p>
                            </div>
                            {progress}
                            {header}
                        </div>
                        <div className="review-content__loading layout-middle layout-center">
                            <LoadingSpinner additionalClasses="fa-3x" />
                        </div>
                    </div>
                </div>
            );
        }

        if (!campaign_content?.id && approved) {
            return (
                <div className="review-content">
                    <div className="review-content__panel review-content__panel--short">
                        <div className='review-content__header_wrapper'>
                            <div className="review-content__header">
                                <p className="review-content__header__text">Content Approval</p>
                            </div>
                            {progress}
                            {header}
                        </div>
                        <NoPendingContent />
                    </div>
                </div>
            );
        }

        return (
            <div className="review-content">
                <div className="review-content__panel">
                    <div className='review-content__header_wrapper'>
                        <div className="review-content__header">
                            <p className="review-content__header__text">Content Approval</p>
                        </div>
                        {progress}
                        {header}
                    </div>

                    { id ? <FeatureFlag
                        flagKey="upload-to-tiktok-csf-enabled"
                        renderFeatureCallback={(featureFlagValue) => {
                            return (
                                <ContentReviewWorkflow
                                    skippable
                                    contentId={id}
                                    guidelines={campaign_guidelines}
                                    platform={platform}
                                    socialId={username}
                                    profileImageUrl={profile_image_url}
                                    contentUrl={content_url}
                                    caption={caption}
                                    editRequest={editRequest}
                                    internalMessages={internalMessages}
                                    brandEmails={brandEmails}
                                    isEditsAllowed={isEditsAllowed}
                                    onApprove={this.onApprove}
                                    onReject={this.onReject}
                                    onDoNotDistribute={this.onDoNotDistribute}
                                    onNext={this.nextContent}
                                    onPrev={this.prevContent}
                                    onRequestSubmit={this.onRequestSubmit}
                                    onInternalMessageSubmit={this.onInternalMessageSubmit}
                                    isTransmitting={is_content_approval_transmitting}
                                    brandApprovalDueDate={brand_approval_due_date}
                                    campaignSlug={this.props.params.campaignSlug}
                                    isLast={isLast}
                                    isFirst={isFirst}
                                    isTiktokCSFEnabled={
                                        featureFlagValue === 'on' || featureFlagValue === true
                                    }
                                />
                            );
                        }}
                        renderDefaultCallback={() => (
                            <ContentReviewWorkflow
                                skippable
                                contentId={id}
                                guidelines={campaign_guidelines}
                                platform={platform}
                                socialId={username}
                                profileImageUrl={profile_image_url}
                                contentUrl={content_url}
                                caption={caption}
                                editRequest={editRequest}
                                internalMessages={internalMessages}
                                brandEmails={brandEmails}
                                isEditsAllowed={isEditsAllowed}
                                onApprove={this.onApprove}
                                onReject={this.onReject}
                                onDoNotDistribute={this.onDoNotDistribute}
                                onNext={this.nextContent}
                                onPrev={this.prevContent}
                                onRequestSubmit={this.onRequestSubmit}
                                onInternalMessageSubmit={this.onInternalMessageSubmit}
                                isTransmitting={is_content_approval_transmitting}
                                brandApprovalDueDate={brand_approval_due_date}
                                campaignSlug={this.props.params.campaignSlug}
                                isLast={isLast}
                                isFirst={isFirst}
                                isTiktokCSFEnabled={false}
                            />
                        )}
                    /> : 
                        <NoSubmittedContent/>
                    }
                </div>
            </div>
        );
    }
}

export default connect(
    R.pick([
        'campaign',
        'campaign_content_progress',
        'campaign_content',
        'campaign_guidelines',
        'is_content_approval_transmitting'
    ]),
    R.pick(
        [
            'fetchCampaign',
            'fetchCampaignContentProgress',
            'fetchCampaignContent',
            'fetchOldestCampaignContent',
            'fetchCampaignGuidelines',
            'clearContentReviewData',
            'approveContent',
            'rejectContent',
            'doNotDistributeContent',
            'gotoListView',
            'beginContentApprovalTransmitting',
            'endContentApprovalTransmitting'
        ],
        CampaignDucks
    )
)(ReviewContent);



// WEBPACK FOOTER //
// ./brand/content_approval/review_content.js